import React, {useEffect, useMemo, useState} from "react";
import {Badge, Button, Icon, Table, TableData} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {PencilIcon} from "@heroicons/react/16/solid";
import {OptionGroup} from "@/data/entities/option";
import {OptionGroupsLookup} from "@widgets/menuEditor/optionGroups/optionGroupsLookup";
import OptionGroupTypeLabel from "@widgets/menuEditor/optionGroups/OptionGroupTypeLabel";

type Props = {
    optionGroups: OptionGroup[], // Sorted and filtered
    lookup: OptionGroupsLookup,
    isFetching: boolean,
    onEdit: (group: OptionGroup) => void,
};

const OptionGroupsTable = ({optionGroups, lookup, isFetching, onEdit}: Props) => {
    const intl = useIntl();
    const [data, setData] = useState<OptionGroup[]>(optionGroups);

    // Follow the menu after a change, and the search
    useEffect(() => setData(optionGroups), [optionGroups]);

    // Several groups can have the same name (e.g. from Sitedish): show the ID
    const duplicateNames: Set<string> = useMemo(() => {
        const seen = new Set<string>();
        const duplicates = new Set<string>();
        optionGroups.forEach(group => (seen.has(group.name) ? duplicates : seen).add(group.name));
        return duplicates;
    }, [optionGroups]);

    return (
        <Table<OptionGroup>
            columns={[
                {
                    id: "name",
                    label: {displayName: intl.formatMessage({id: "optionGroup"})},
                    primary: true,
                    formatter: (name: string, group: TableData<OptionGroup>) => (
                        <div className={"flex flex-col"}>
                            <span className={"font-medium text-gray-900 dark:text-white"}>
                                {name}
                                {
                                    duplicateNames.has(name) &&
                                    <span className={"ml-2 font-normal text-gray-400"}>#{group.id}</span>
                                }
                            </span>
                            <span className={"text-sm text-gray-500 dark:text-slate-400"}>
                                <FormattedMessage
                                    id={"usedByProducts"}
                                    values={{count: lookup.productCountByGroup.get(group.id) ?? 0}}
                                />
                            </span>
                        </div>
                    ),
                },
                {
                    id: "type",
                    label: {displayName: intl.formatMessage({id: "type"})},
                    visibility: "md",
                    formatter: (_, group: TableData<OptionGroup>) => (
                        <Badge size={"xs"} color={group.type === "required" ? "primary" : "secondary"}>
                            <OptionGroupTypeLabel group={group}/>
                        </Badge>
                    ),
                },
                {
                    id: "optionIds",
                    label: {displayName: intl.formatMessage({id: "options"})},
                    align: "right",
                    formatter: (optionIds: number[]) => (
                        <FormattedMessage id={"nOptions"} values={{count: optionIds.length}}/>
                    ),
                },
                {
                    id: "id",
                    label: {
                        displayName: intl.formatMessage({id: "actions"}),
                        hide: true,
                    },
                    align: "right",
                    formatter: (_, group: TableData<OptionGroup>) => (
                        <div className={"inline-block"}>
                            <Button
                                color={"secondary"}
                                disabled={isFetching}
                                iconBefore={<Icon icon={PencilIcon}/>}
                                buttonAction={{
                                    as: "button",
                                    actionProps: {
                                        onClick: () => onEdit(group),
                                    },
                                }}
                                size={"xs"}
                                breakpoints={{md: {size: "sm"}}}
                            >
                                <FormattedMessage id={"edit"}/>
                            </Button>
                        </div>
                    ),
                },
            ]}
            data={data}
            setData={setData}
            loading={isFetching && data.length === 0}
            size={"compact"}
            breakpoints={{md: {size: "normal"}}}
        />
    );
};

export default OptionGroupsTable;